import React from "react";
import type { AppConfig } from "./contexts/app/appConfig";
import { AppConfigContext } from "./contexts/app/appConfig";
import { zekayTheme } from "./styles/globalStyles";
import { ConfirmationModalProvider } from "./contexts/app/confirmation";
import { AlertProvider } from "./contexts/app/notificationToast";
import { AuthProvider, PortalProvider, RgpdProvider } from "./contexts/api";
import { CssBaseline, CssVarsProvider } from "@mui/joy";
import { Route, Routes, Navigate, Outlet } from "react-router-dom";
import LoginPage from "./components/login/LoginPage";
import PortalPage from "./components/portal/PortalPage";
import TheoryTestRoutePage from "./components/theorytest/TheoryTestRoutePage";
import UnregisterPage from "./components/gdpr/UnregisterPage";
import { ProtectedRoutes } from "./components/login/ProtectedRoutes";

export default function App() {
  const [appConfig, setAppConfig] = React.useState<AppConfig | null>(null);

  React.useEffect(() => {
    const env = (import.meta.env.VITE_APP_ENV || "").toLowerCase();
    document.title = env === "dev" ? "Zekay Portal (Dev)" : "Zekay Portal";

    fetch(`${window.location.origin}/config.json`)
      .then((response) => response.json())
      .then((data: AppConfig) => setAppConfig(data))
      .catch((error) => console.error("Erreur lors du chargement de la configuration :", error));
  }, []);

  if (!appConfig) {
    return <div>Chargement de la configuration…</div>;
  }

  return (
    <ConfirmationModalProvider>
      <AppConfigContext.Provider value={appConfig}>
        <CssVarsProvider theme={zekayTheme}>
          <CssBaseline />
          <AlertProvider>
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/newsletter/unregister"
                  element={
                    <RgpdProvider>
                      <UnregisterPage />
                    </RgpdProvider>
                  }
                />
                <Route path="/" element={<Navigate to="/login" />} />

                <Route
                  element={
                    <ProtectedRoutes>
                      <PortalProvider>
                        <Outlet />
                      </PortalProvider>
                    </ProtectedRoutes>
                  }
                >
                  <Route path="/portal" element={<PortalPage />} />
                  <Route
                    path="/portal/theory-test/:trainingTemplateId"
                    element={<TheoryTestRoutePage />}
                  />
                </Route>

                {/* Any undefined route redirects to /login */}
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            </AuthProvider>
          </AlertProvider>
        </CssVarsProvider>
      </AppConfigContext.Provider>
    </ConfirmationModalProvider>
  );
}
